import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { allBlogPosts, type BlogPost } from '@/mocks/blogData';
import { contactInfo } from '@/mocks/homeData';
import { useFadeIn } from '@/hooks/useFadeIn';
import Breadcrumb from '@/components/base/Breadcrumb';

function BlogHero({ post }: { post: BlogPost }) {
  return (
    <section className="relative w-full h-[380px] md:h-[520px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={post.heroImage}
          alt={post.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
      </div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pb-12 md:pb-16">
        <span className="inline-block text-xs font-semibold text-accent-300 bg-accent-900/30 backdrop-blur-sm px-3 py-1 rounded-full mb-4">
          {post.category}
        </span>
        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-white/70 text-xs md:text-sm">
          <span>{post.date}</span>
          <span className="w-1 h-1 rounded-full bg-white/40"></span>
          <span>{post.readTime}</span>
        </div>
      </div>
    </section>
  );
}

function BlogAuthor({ author }: { author: BlogPost['author'] }) {
  return (
    <div className="flex items-center gap-4 py-6 border-y border-background-200">
      <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
        <img src={author.avatar} alt={author.name} className="w-full h-full object-cover object-top" />
      </div>
      <div>
        <p className="font-semibold text-sm text-foreground-950">{author.name}</p>
        <p className="text-xs text-foreground-500">{author.role}</p>
      </div>
    </div>
  );
}

function BlogContent({ post }: { post: BlogPost }) {
  const { ref, isVisible } = useFadeIn(0.05);

  return (
    <div ref={ref} className={`space-y-8 fade-section ${isVisible ? 'is-visible' : ''}`}>
      {post.content.map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={i} className="text-foreground-700 text-base md:text-lg leading-relaxed">
                {block.text}
              </p>
            );
          case 'heading':
            return (
              <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-tight mt-10">
                {block.text}
              </h2>
            );
          case 'quote':
            return (
              <blockquote key={i} className="border-l-4 border-accent-400 pl-6 py-2 my-6">
                <p className="font-heading italic text-foreground-600 text-lg md:text-xl leading-relaxed">
                  "{block.text}"
                </p>
                <p className="text-xs text-foreground-400 mt-3 font-medium">— Dra. Marihana Pasquali</p>
              </blockquote>
            );
          case 'list':
            return (
              <ul key={i} className="space-y-3">
                {block.items?.map((item, j) => (
                  <li key={j} className="flex items-start gap-3 text-foreground-700 text-base leading-relaxed">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-accent-50 rounded-full mt-0.5">
                      <i className="ri-check-line text-accent-600 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            );
          case 'image':
            return (
              <figure key={i} className="rounded-xl overflow-hidden my-8">
                <img
                  src={block.src}
                  alt={block.alt || ''}
                  className="w-full h-auto object-cover"
                />
                {block.caption && (
                  <figcaption className="text-xs text-foreground-400 text-center mt-2 bg-background-100 py-2">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

function BlogTags({ tags }: { tags: string[] }) {
  return (
    <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
      {tags.map((tag) => (
        <span key={tag} className="text-xs font-medium text-secondary-700 bg-secondary-50 px-3 py-1.5 rounded-full">
          {tag}
        </span>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPost }: { currentPost: BlogPost }) {
  const related = allBlogPosts.filter(
    (p) => p.id !== currentPost.id && currentPost.relatedIds.includes(p.id)
  );
  const { ref, isVisible } = useFadeIn(0.1);

  if (!related.length) return null;

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-10 fade-section ${isVisible ? 'is-visible' : ''}`}>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Conteúdos relacionados
          </h3>
          <p className="text-foreground-500 text-sm mt-2">Continue aprendendo sobre saúde e estética bucal</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {related.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-background-50 rounded-xl overflow-hidden card-hover"
            >
              <div className="relative overflow-hidden h-44 img-zoom">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-5">
                <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">
                  {post.category}
                </span>
                <h4 className="font-heading text-sm font-bold text-foreground-950 mt-3 mb-2 leading-snug group-hover:text-primary-600 transition-colors duration-300">
                  {post.title}
                </h4>
                <span className="text-xs text-foreground-400">{post.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogCTA() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
          Gostou do conteúdo?
        </h3>
        <p className="text-foreground-600 text-base leading-relaxed mb-8">
          Agende sua avaliação com a Dra. Marihana Pasquali e descubra como transformar seu sorriso com segurança, conforto e resultados naturais na Faria Lima.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Li%20um%20artigo%20no%20blog%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
          >
            <i className="ri-whatsapp-line mr-2"></i>
            Agendar Avaliação
          </a>
          <Link
            to="/#servicos"
            className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
          >
            Conhecer Tratamentos
          </Link>
        </div>
      </div>
    </section>
  );
}

function BlogPostSchema({ post }: { post: BlogPost }) {
  const articleBody = post.content
    .map((block) => {
      if (block.type === 'list') return (block.items || []).join(' ');
      if (block.type === 'paragraph' || block.type === 'heading' || block.type === 'quote') return block.text || '';
      return '';
    })
    .filter(Boolean)
    .join(' ');

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.heroImage,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Person',
      name: post.author.name,
      jobTitle: 'Cirurgiã Dentista — CRO-SP 172094',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dra. Marihana Pasquali',
      logo: {
        '@type': 'ImageObject',
        url: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/f075322b-f3ac-4364-a5fe-73fa28de4de4_compressed_DSC07751---Copia---Copia.webp',
      },
    },
    keywords: post.tags.join(', '),
    articleSection: post.category,
    articleBody,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function BlogPage() {
  const { slug } = useParams<{ slug: string }>();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const post = allBlogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
          <p className="text-foreground-600 mb-6">O conteúdo que você procura não está disponível.</p>
          <Link to="/" className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3 rounded-full transition-all duration-300 cursor-pointer inline-block">
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <BlogPostSchema post={post} />
      <BlogHero post={post} />

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <Breadcrumb
          items={[
            { label: 'Início', href: '/' },
            { label: 'Blog', href: '/#blog' },
            { label: post.title },
          ]}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
        <BlogAuthor author={post.author} />
        <BlogContent post={post} />
        <BlogTags tags={post.tags} />
      </article>

      <RelatedPosts currentPost={post} />
      <BlogCTA />
    </main>
  );
}